import { useState, useEffect, useRef } from "react";
import {
  Loader2, Check, Upload, Trash2, RotateCcw, Camera, X, ZoomIn, FileText,
} from "lucide-react";
import { api, getErrorMessage } from "@/lib/api";

const REQUIRED_DOCS = ["pan_front", "aadhaar_front", "aadhaar_back", "selfie"];

const INPUT = "w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm outline-none transition focus:border-primary";

function Field({ label, hint, error, children }) {
  return (
    <div>
      {label && <label className="text-xs text-muted-foreground">{label}</label>}
      <div className="relative mt-1.5">{children}</div>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
      {error && <p className="mt-1 text-xs text-down">{error}</p>}
    </div>
  );
}

function TextInput({ testid, ...props }) {
  return <input data-testid={testid} {...props} className={`${INPUT} ${props.className || ""}`} />;
}

function SelectInput({ testid, children, ...props }) {
  return (
    <select data-testid={testid} {...props} className={INPUT}>
      {children}
    </select>
  );
}

function ErrorBox({ msg }) {
  if (!msg) return null;
  return (
    <div className="rounded-lg border border-down/40 bg-down/10 px-3 py-2 text-xs text-down" data-testid="kyc-wizard-error">
      {msg}
    </div>
  );
}

/* ========================== Personal Details ========================== */
function PersonalDetailsForm({ onDone }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    full_name: "", date_of_birth: "", gender: "male",
    nationality: "Indian", country_of_birth: "India", occupation: "",
    annual_income_range: "0-5L", source_of_funds: "salary",
    purpose_of_use: "", pep_declaration: false, pan_number: "",
    address: { line1: "", line2: "", city: "", state: "", country: "India", pincode: "" },
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));

  async function submit(e) {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/api/kyc/personal", form);
      onDone();
    } catch (e2) { setErr(getErrorMessage(e2)); }
    finally { setLoading(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="kyc-personal-form">
      <div>
        <p className="text-sm font-semibold">Personal details</p>
        <p className="mt-1 text-xs text-muted-foreground">Exactly as per your government ID.</p>
      </div>

      <Field label="Full name (as per PAN)">
        <TextInput required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} testid="kyc-full-name" />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Date of birth">
          <TextInput type="date" required value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} testid="kyc-dob" />
        </Field>
        <Field label="Gender">
          <SelectInput value={form.gender} onChange={(e) => set("gender", e.target.value)} testid="kyc-gender">
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
            <option value="prefer_not_to_say">Prefer not to say</option>
          </SelectInput>
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Nationality">
          <TextInput required value={form.nationality} onChange={(e) => set("nationality", e.target.value)} testid="kyc-nationality" />
        </Field>
        <Field label="Country of birth">
          <TextInput required value={form.country_of_birth} onChange={(e) => set("country_of_birth", e.target.value)} testid="kyc-country-birth" />
        </Field>
      </div>

      <Field label="Occupation">
        <TextInput required value={form.occupation} onChange={(e) => set("occupation", e.target.value)} testid="kyc-occupation" />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Annual income">
          <SelectInput value={form.annual_income_range} onChange={(e) => set("annual_income_range", e.target.value)} testid="kyc-income">
            <option value="0-5L">0-5L</option>
            <option value="5-10L">5-10L</option>
            <option value="10-25L">10-25L</option>
            <option value="25-50L">25-50L</option>
            <option value="50L-1Cr">50L-1Cr</option>
            <option value="1Cr+">1Cr+</option>
          </SelectInput>
        </Field>
        <Field label="Source of funds">
          <SelectInput value={form.source_of_funds} onChange={(e) => set("source_of_funds", e.target.value)} testid="kyc-funds">
            <option value="salary">Salary</option>
            <option value="business">Business</option>
            <option value="savings">Savings</option>
            <option value="investments">Investments</option>
            <option value="inheritance">Inheritance</option>
            <option value="gift">Gift</option>
            <option value="other">Other</option>
          </SelectInput>
        </Field>
      </div>

      <Field label="Purpose of using PaisaCX">
        <TextInput required value={form.purpose_of_use} onChange={(e) => set("purpose_of_use", e.target.value)} testid="kyc-purpose" placeholder="e.g. Long-term investment" />
      </Field>

      <Field label="PAN number (India)">
        <TextInput value={form.pan_number} onChange={(e) => set("pan_number", e.target.value.toUpperCase())} testid="kyc-pan" placeholder="ABCDE1234F" maxLength={10} />
      </Field>

      <div className="border-t border-border pt-4">
        <p className="mb-3 text-sm font-semibold">Residential address</p>
        <Field label="Address line 1">
          <TextInput required value={form.address.line1} onChange={(e) => setAddr("line1", e.target.value)} testid="kyc-addr-line1" />
        </Field>
        <div className="mt-3">
          <Field label="Address line 2 (optional)">
            <TextInput value={form.address.line2} onChange={(e) => setAddr("line2", e.target.value)} testid="kyc-addr-line2" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Field label="City">
            <TextInput required value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} testid="kyc-addr-city" />
          </Field>
          <Field label="State">
            <TextInput required value={form.address.state} onChange={(e) => setAddr("state", e.target.value)} testid="kyc-addr-state" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Field label="Country">
            <TextInput required value={form.address.country} onChange={(e) => setAddr("country", e.target.value)} testid="kyc-addr-country" />
          </Field>
          <Field label="Pincode">
            <TextInput required value={form.address.pincode} onChange={(e) => setAddr("pincode", e.target.value)} testid="kyc-addr-pincode" />
          </Field>
        </div>
      </div>

      <label className="flex items-start gap-2.5 pt-2 cursor-pointer">
        <input
          type="checkbox"
          checked={form.pep_declaration}
          onChange={(e) => set("pep_declaration", e.target.checked)}
          data-testid="kyc-pep"
          className="mt-1 w-4 h-4 rounded accent-primary"
        />
        <span className="text-xs leading-relaxed text-muted-foreground">
          I declare that I am a Politically Exposed Person (PEP) or related to one.
          <br /><span className="text-[11px]">(Leave unchecked if not applicable.)</span>
        </span>
      </label>

      <ErrorBox msg={err} />
      <button
        type="submit"
        disabled={loading}
        data-testid="kyc-personal-submit"
        className="brand-gradient inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60"
      >
        {loading && <Loader2 size={14} className="animate-spin" />}
        Continue
      </button>
    </form>
  );
}

/* ========================== Image Viewer ========================== */
function ImageViewer({ src, onClose, label }) {
  if (!src) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 grid place-items-center p-6" onClick={onClose} data-testid="kyc-image-viewer">
      <button onClick={onClose} className="absolute top-5 right-5 text-white bg-white/10 hover:bg-white/20 rounded-full w-10 h-10 grid place-items-center" aria-label="Close">
        <X size={18} />
      </button>
      <div className="relative max-w-[92vw] max-h-[88vh] text-center">
        <img src={src} alt={label} className="max-w-full max-h-[84vh] rounded-lg shadow-2xl mx-auto" onClick={(e) => e.stopPropagation()} />
        <div className="mt-3 text-white/80 text-[13px]">{label}</div>
      </div>
    </div>
  );
}

/* ========================== Doc Upload Card ========================== */
function DocUploadCard({ docType, label, hint, onView, onUploaded, onRemoved, initialUploaded }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(!!initialUploaded);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { setUploaded(!!initialUploaded); }, [initialUploaded]);
  useEffect(() => {
    if (!file) { setPreviewUrl(""); return; }
    const u = URL.createObjectURL(file);
    setPreviewUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  async function upload(f) {
    setErr(""); setUploading(true);
    try {
      const form = new FormData();
      form.append("doc_type", docType);
      form.append("file", f);
      await api.post("/api/kyc/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded(true); onUploaded?.(docType);
    } catch (e) { setErr(getErrorMessage(e)); setUploaded(false); }
    finally { setUploading(false); }
  }

  function pick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setUploaded(false); upload(f);
  }
  function remove() {
    setFile(null); setUploaded(false); setErr("");
    onRemoved?.(docType);
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <div
      className={`rounded-xl border p-4 transition ${
        uploaded ? "border-up/30 bg-up/5"
        : err ? "border-down/30 bg-down/5"
        : "border-border bg-surface-2/50"
      }`}
      data-testid={`kyc-doc-card-${docType}`}
    >
      <input ref={inputRef} type="file" className="hidden" accept="image/*,application/pdf" onChange={pick} />
      <div className="flex items-center gap-4">
        {previewUrl ? (
          <button type="button" onClick={() => onView?.({ src: previewUrl, label })}
                  data-testid={`kyc-doc-thumb-${docType}`}
                  className="relative shrink-0 w-[88px] h-[88px] rounded-lg overflow-hidden bg-surface-2 border border-border group cursor-zoom-in">
            {file?.type === "application/pdf" ? (
              <div className="w-full h-full grid place-items-center text-[10px] font-semibold text-muted-foreground">PDF</div>
            ) : (
              <img src={previewUrl} alt="" className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition grid place-items-center">
              <ZoomIn size={18} className="text-white opacity-0 group-hover:opacity-100" />
            </div>
          </button>
        ) : (
          <button type="button" onClick={() => inputRef.current?.click()}
                  data-testid={`kyc-doc-pick-${docType}`}
                  className="shrink-0 w-[88px] h-[88px] rounded-lg border-2 border-dashed border-border grid place-items-center text-muted-foreground hover:border-primary hover:text-primary transition">
            <Upload size={18} />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[14px] font-semibold text-foreground truncate">
            {label}
            {uploaded && (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-up bg-up/15 px-2 py-0.5 rounded-full ml-1">
                <Check size={10} /> Uploaded
              </span>
            )}
          </div>
          <div className="text-[12px] text-muted-foreground mt-0.5 truncate">
            {file ? `${file.name} · ${(file.size / 1024 / 1024).toFixed(2)} MB` : (hint || "JPG, PNG, WEBP or PDF (max 8 MB)")}
          </div>
          <div className="mt-2.5 flex items-center gap-3">
            {file ? (
              <>
                <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
                        className="text-[12px] text-primary hover:underline font-semibold flex items-center gap-1 disabled:opacity-50"
                        data-testid={`kyc-doc-replace-${docType}`}>
                  <RotateCcw size={12} /> Replace
                </button>
                <button type="button" onClick={remove} disabled={uploading}
                        className="text-[12px] text-down hover:underline font-semibold flex items-center gap-1 disabled:opacity-50"
                        data-testid={`kyc-doc-remove-${docType}`}>
                  <Trash2 size={12} /> Remove
                </button>
              </>
            ) : (
              <button type="button" onClick={() => inputRef.current?.click()}
                      className="text-[12px] text-primary hover:underline font-semibold"
                      data-testid={`kyc-doc-browse-${docType}`}>
                Browse file
              </button>
            )}
            {uploading && (
              <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Loader2 size={11} className="animate-spin" /> Uploading…
              </span>
            )}
          </div>
          {err && <div className="mt-2 text-[12px] text-down">{err}</div>}
        </div>
      </div>
    </div>
  );
}

/* ========================== Selfie Capture ========================== */
function SelfieCapture({ onView, onUploaded, initialUploaded }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(!!initialUploaded);
  const [err, setErr] = useState("");
  const [camOn, setCamOn] = useState(false);

  useEffect(() => { setUploaded(!!initialUploaded); }, [initialUploaded]);

  function stopCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCamOn(false);
  }
  useEffect(() => () => stopCamera(), []);

  async function openCamera() {
    setErr("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setErr("Camera is not supported in this browser. Try Chrome or Safari."); return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } }, audio: false,
      });
      streamRef.current = s; setCamOn(true);
      setTimeout(async () => {
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          try { await videoRef.current.play(); } catch (_) { /* ignore */ }
        }
      }, 50);
    } catch (e) {
      setErr("Camera access denied. Allow camera permission and try again.");
    }
  }

  async function uploadBlob(blob, filename) {
    const f = new File([blob], filename, { type: blob.type || "image/jpeg" });
    setUploading(true); setErr("");
    try {
      const form = new FormData();
      form.append("doc_type", "selfie");
      form.append("file", f);
      await api.post("/api/kyc/documents/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded(true); onUploaded?.();
    } catch (e) { setErr(getErrorMessage(e)); setUploaded(false); }
    finally { setUploading(false); }
  }

  async function capture() {
    try {
      const v = videoRef.current; const c = canvasRef.current;
      if (!v || !c || !v.videoWidth) { setErr("Camera not ready, try again."); return; }
      c.width = v.videoWidth; c.height = v.videoHeight;
      c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
      const blob = await new Promise((res, rej) => c.toBlob((b) => b ? res(b) : rej(new Error("Capture failed")), "image/jpeg", 0.92));
      setPreviewUrl(URL.createObjectURL(blob));
      stopCamera();
      await uploadBlob(blob, `selfie-${Date.now()}.jpg`);
    } catch (e) { setErr("Could not capture, try again."); }
  }

  function retake() { setUploaded(false); setPreviewUrl(""); setErr(""); openCamera(); }

  return (
    <div className={`rounded-xl border p-4 transition ${
      uploaded ? "border-up/30 bg-up/5"
      : err ? "border-down/30 bg-down/5"
      : "border-border bg-surface-2/50"
    }`} data-testid="kyc-selfie-card">
      <canvas ref={canvasRef} className="hidden" />
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-[120px] h-[120px] rounded-lg overflow-hidden bg-surface-2 relative">
          {camOn && !previewUrl && (
            <video ref={videoRef} playsInline muted autoPlay className="w-full h-full object-cover scale-x-[-1]" data-testid="kyc-selfie-video" />
          )}
          {previewUrl && (
            <button type="button" onClick={() => onView?.({ src: previewUrl, label: "Live Selfie" })}
                    className="w-full h-full cursor-zoom-in relative group" data-testid="kyc-selfie-thumb">
              <img src={previewUrl} alt="Selfie" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition grid place-items-center">
                <ZoomIn size={18} className="text-white opacity-0 group-hover:opacity-100" />
              </div>
            </button>
          )}
          {!camOn && !previewUrl && (
            <div className="w-full h-full grid place-items-center text-white/70"><Camera size={24} /></div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[14px] font-semibold text-foreground">
            Live Selfie
            {uploaded && (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-up bg-up/15 px-2 py-0.5 rounded-full ml-1">
                <Check size={10} /> Captured
              </span>
            )}
          </div>
          <div className="text-[12px] text-muted-foreground mt-0.5">
            Look at the camera in good light. Gallery upload is not allowed.
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            {!camOn && !uploaded && (
              <button type="button" onClick={openCamera}
                      className="text-[12px] brand-gradient text-primary-foreground px-4 py-2 rounded-md font-semibold flex items-center gap-1.5 hover:brightness-110"
                      data-testid="kyc-selfie-open">
                <Camera size={13} /> Open camera
              </button>
            )}
            {camOn && (
              <>
                <button type="button" onClick={capture} disabled={uploading}
                        className="text-[12px] brand-gradient text-primary-foreground px-4 py-2 rounded-md font-semibold flex items-center gap-1.5 hover:brightness-110 disabled:opacity-60"
                        data-testid="kyc-selfie-capture">
                  <Camera size={13} /> Capture
                </button>
                <button type="button" onClick={stopCamera} className="text-[12px] text-muted-foreground hover:text-foreground font-semibold">
                  Cancel
                </button>
              </>
            )}
            {uploaded && (
              <button type="button" onClick={retake}
                      className="text-[12px] text-primary hover:underline font-semibold flex items-center gap-1"
                      data-testid="kyc-selfie-retake">
                <RotateCcw size={12} /> Retake
              </button>
            )}
            {uploading && (
              <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Loader2 size={11} className="animate-spin" /> Uploading…
              </span>
            )}
          </div>
          {err && <div className="mt-2 text-[12px] text-down">{err}</div>}
        </div>
      </div>
    </div>
  );
}

/* ========================== Documents Form ========================== */
function DocumentsForm({ initialUploaded, onSubmitted, faceMatchRequired, onProgress }) {
  const [viewer, setViewer] = useState(null);
  const [uploaded, setUploaded] = useState(initialUploaded || {});
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");
  const [faceBusy, setFaceBusy] = useState(false);
  const [faceMatch, setFaceMatch] = useState(null);

  useEffect(() => {
    if (!faceMatchRequired) return;
    api.get("/api/kyc/status").then(({ data }) => {
      setFaceMatch(data.face_match || null);
    }).catch(() => {});
  }, [faceMatchRequired]);

  const allDone = REQUIRED_DOCS.every((d) => uploaded[d]);
  const faceOk = !faceMatchRequired || faceMatch?.verified;

  useEffect(() => {
    onProgress?.({ uploaded, faceOk: Boolean(faceOk) });
  }, [uploaded, faceOk, onProgress]);

  const markUploaded = (docType) => setUploaded((u) => ({ ...u, [docType]: true }));
  const markRemoved = (docType) => setUploaded((u) => { const n = { ...u }; delete n[docType]; return n; });

  async function runFaceMatch() {
    setFaceBusy(true);
    setSubmitErr("");
    try {
      const { data } = await api.post("/api/kyc/face-match");
      setFaceMatch(data.face_match || { verified: data.verified, message: data.message });
      if (!data.verified) setSubmitErr(data.message || "Face match failed — retake your selfie.");
    } catch (e) {
      setSubmitErr(getErrorMessage(e));
    } finally {
      setFaceBusy(false);
    }
  }

  async function handleSubmit() {
    if (!allDone || !faceOk || submitting) return;
    setSubmitErr(""); setSubmitting(true);
    try {
      await api.post("/api/kyc/submit");
      onSubmitted();
    } catch (e) { setSubmitErr(getErrorMessage(e)); }
    finally { setSubmitting(false); }
  }

  return (
    <div className="space-y-5" data-testid="kyc-docs-form">
      <div>
        <h3 className="text-sm font-semibold">Upload documents</h3>
        <p className="text-[13px] text-muted-foreground mt-1">
          PAN card, Aadhaar (front & back) and a live selfie. JPG/PNG/WEBP/PDF up to 8 MB.
        </p>
      </div>

      <div className="space-y-3">
        <div className="text-xs text-muted-foreground">PAN Card</div>
        <DocUploadCard docType="pan_front" label="PAN card" hint="Front side, clear and fully visible."
                       onView={setViewer} onUploaded={markUploaded} onRemoved={markRemoved}
                       initialUploaded={uploaded.pan_front} />

        <div className="text-xs text-muted-foreground mt-4">Aadhaar Card</div>
        <DocUploadCard docType="aadhaar_front" label="Aadhaar — front" hint="Side with photo & name."
                       onView={setViewer} onUploaded={markUploaded} onRemoved={markRemoved}
                       initialUploaded={uploaded.aadhaar_front} />
        <DocUploadCard docType="aadhaar_back" label="Aadhaar — back" hint="Side with address & QR."
                       onView={setViewer} onUploaded={markUploaded} onRemoved={markRemoved}
                       initialUploaded={uploaded.aadhaar_back} />

        <div className="text-xs text-muted-foreground mt-4">Selfie</div>
        <SelfieCapture onView={setViewer} onUploaded={() => markUploaded("selfie")} initialUploaded={uploaded.selfie} />
      </div>

      <div className="rounded-lg bg-surface-2 border border-border px-4 py-3 text-[13px] text-muted-foreground">
        {allDone ? (
          <span className="text-foreground font-medium">
            {faceMatchRequired && !faceOk
              ? "Documents uploaded — complete face verification below."
              : "All required documents uploaded. Submit for review."}
          </span>
        ) : (
          <>{REQUIRED_DOCS.filter((d) => uploaded[d]).length} of {REQUIRED_DOCS.length} required documents uploaded.</>
        )}
      </div>

      {faceMatchRequired && allDone && (
        <div className="rounded-lg border border-border bg-surface-2/50 px-4 py-4 space-y-3">
          <div className="text-[14px] font-semibold text-foreground">Signzy face verification</div>
          <p className="text-[12px] text-muted-foreground">Your selfie is compared to your Aadhaar photo before admin review.</p>
          {faceMatch?.verified ? (
            <p className="text-[13px] text-up font-medium flex items-center gap-1">
              <Check size={14} /> Verified
              {faceMatch.match_percentage != null && ` (${faceMatch.match_percentage}%)`}
            </p>
          ) : (
            <>
              {faceMatch?.message && <p className="text-[12px] text-down">{faceMatch.message}</p>}
              <button type="button" onClick={runFaceMatch} disabled={faceBusy}
                className="text-[13px] brand-gradient text-primary-foreground px-4 py-2.5 rounded-md font-semibold disabled:opacity-60 inline-flex items-center gap-2">
                {faceBusy && <Loader2 size={14} className="animate-spin" />}
                Run face verification
              </button>
            </>
          )}
        </div>
      )}

      {submitErr && (
        <div className="text-[13px] text-down bg-down/10 border border-down/30 rounded-lg px-4 py-3">
          {submitErr}
        </div>
      )}

      <button type="button" onClick={handleSubmit} disabled={!allDone || !faceOk || submitting}
              data-testid="kyc-docs-submit"
              className="brand-gradient inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
        {submitting && <Loader2 size={16} className="animate-spin" />}
        {submitting ? "Submitting…" : "Submit for review"}
      </button>

      <ImageViewer src={viewer?.src} label={viewer?.label} onClose={() => setViewer(null)} />
    </div>
  );
}

/* ========================== Status Banner ========================== */
function StatusBanner({ status, reason }) {
  const map = {
    approved: { c: "border-up/40 bg-up/10 text-up", label: "KYC approved — you can start trading." },
    pending_review: { c: "border-warning/40 bg-warning/10 text-warning", label: "Under review — usually takes 24–48 hours." },
    rejected: { c: "border-down/40 bg-down/10 text-down", label: `KYC rejected: ${reason || "please re-upload your documents"}` },
  };
  const conf = map[status];
  if (!conf) return null;
  return (
    <div className={`rounded-xl border px-5 py-4 text-sm ${conf.c}`} data-testid="kyc-banner">
      {conf.label}
    </div>
  );
}

const STATUS_BOX = {
  approved: { label: "Verified", tone: "text-up" },
  pending_review: { label: "Under review", tone: "text-warning" },
  rejected: { label: "Rejected", tone: "text-down" },
  personal_done: { label: "In progress", tone: "text-accent" },
  not_started: { label: "Unverified", tone: "text-accent" },
};

/** Old PaisaCX identity-verification header: status box, progress bar and step chips. */
export function KycHeader({ status = "not_started", steps = null, subtitle }) {
  const box = STATUS_BOX[status] || STATUS_BOX.not_started;
  const doneCount = steps ? steps.filter((s) => s.done).length : status === "approved" ? 1 : 0;
  const pct = steps ? doneCount / steps.length : status === "approved" ? 1 : 0;
  const activeIdx = steps ? steps.findIndex((s) => !s.done) : -1;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Identity verification</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {subtitle || "Aadhaar, PAN, face match and personal details — reviewed by our compliance team."}
          </p>
        </div>
        <div className="rounded-lg border border-border bg-surface/50 px-3 py-1.5 text-right" data-testid="kyc-status-box">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Status</p>
          <p className={`text-sm font-semibold ${box.tone}`}>{box.label}</p>
        </div>
      </div>
      <div className="h-1 overflow-hidden rounded-full bg-surface-2">
        <div className="brand-gradient h-full transition-all duration-500" style={{ width: `${Math.round(pct * 100)}%` }} />
      </div>
      {steps && (
        <div className="flex flex-wrap gap-2" data-testid="kyc-steps">
          {steps.map((s, i) => (
            <span
              key={s.label}
              className={`rounded-md px-2.5 py-1 text-xs ${
                i === activeIdx ? "bg-primary/[0.15] text-primary" : s.done ? "text-up" : "text-muted-foreground"
              }`}
            >
              <span className="num mr-1 opacity-70">{s.done ? "✓" : i + 1}</span>
              {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ========================== Main Wizard ========================== */
export default function KycWizard({ faceMatchRequired = false }) {
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | personal | docs | review | approved | rejected

  async function load() {
    try {
      const { data: d } = await api.get("/api/user/overview");
      setData(d);
      const status = d.user?.kyc_status || "not_started";
      if (status === "approved") setPhase("approved");
      else if (status === "pending_review") setPhase("review");
      else if (status === "rejected") setPhase("rejected");
      else if (status === "personal_done") setPhase("docs");
      else setPhase("personal");
    } catch (e) {
      setPhase("personal");
    }
  }

  useEffect(() => { load(); }, []);

  const initialDocs = (() => {
    const map = {};
    for (const d of (data?.documents || [])) map[d.doc_type] = true;
    return map;
  })();

  const [progress, setProgress] = useState(null);
  const docs = progress?.uploaded || initialDocs;
  const submitted = phase === "review" || phase === "approved";
  const steps = [
    { label: "Personal", done: phase !== "personal" },
    { label: "PAN", done: submitted || Boolean(docs.pan_front) },
    { label: "Aadhaar", done: submitted || Boolean(docs.aadhaar_front && docs.aadhaar_back) },
    { label: faceMatchRequired ? "Face match" : "Selfie", done: submitted || Boolean(docs.selfie && (progress ? progress.faceOk : !faceMatchRequired)) },
    { label: "Submit", done: submitted },
  ];
  const status = data?.user?.kyc_status || "not_started";

  if (phase === "loading") {
    return (
      <div className="space-y-5" data-testid="kyc-loading">
        <KycHeader status="not_started" />
        <div className="skeleton h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kyc-wizard">
      <KycHeader status={status} steps={steps} />
      <StatusBanner status={status} reason={data?.user?.kyc_rejection_reason} />

      {phase === "personal" && (
        <div className="rounded-xl border border-border bg-surface/50 p-5">
          <PersonalDetailsForm onDone={() => load()} />
        </div>
      )}

      {phase === "docs" && (
        <div className="rounded-xl border border-border bg-surface/50 p-5">
          <DocumentsForm initialUploaded={initialDocs} onSubmitted={() => load()} faceMatchRequired={faceMatchRequired} onProgress={setProgress} />
        </div>
      )}

      {(phase === "review" || phase === "approved" || phase === "rejected") && (
        <>
          {data?.kyc_profile && (
            <div className="rounded-xl border border-border bg-surface/50 p-5">
              <h3 className="mb-4 text-sm font-semibold">Personal details</h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-[13px]">
                {[
                  ["Full name", data.kyc_profile.full_name],
                  ["Date of birth", data.kyc_profile.date_of_birth],
                  ["Gender", data.kyc_profile.gender],
                  ["Nationality", data.kyc_profile.nationality],
                  ["Occupation", data.kyc_profile.occupation],
                  ["Annual income", data.kyc_profile.annual_income_range],
                  ["Source of funds", data.kyc_profile.source_of_funds],
                  ["PAN", data.kyc_profile.pan_number || "—"],
                  ["PEP", data.kyc_profile.pep_declaration ? "Yes" : "No"],
                  ["City", data.kyc_profile.address?.city],
                  ["State", data.kyc_profile.address?.state],
                  ["Pincode", data.kyc_profile.address?.pincode],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-muted-foreground text-[11px] uppercase tracking-wider">{k}</div>
                    <div className="text-foreground font-medium">{v || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-border bg-surface/50 p-5">
            <h3 className="mb-4 text-sm font-semibold">Documents</h3>
            {(data?.documents || []).length === 0 ? (
              <div className="text-muted-foreground text-[13px]">No documents uploaded yet.</div>
            ) : (
              <ul className="divide-y divide-border">
                {data.documents.map((d) => (
                  <li key={d.doc_type} className="flex items-center gap-3 py-3" data-testid={`kyc-doc-${d.doc_type}`}>
                    <FileText size={16} className="text-primary shrink-0" />
                    <div className="flex-1">
                      <div className="text-[13px] font-semibold text-foreground capitalize">{d.doc_type.replace(/_/g, " ")}</div>
                      <div className="text-[11px] text-muted-foreground">{d.file_name}</div>
                    </div>
                    <span className="text-[11px] text-up font-semibold">Uploaded</span>
                  </li>
                ))}
              </ul>
            )}

            {phase === "rejected" && (
              <button type="button"
                      onClick={async () => { await api.post("/api/user/kyc/resubmit"); load(); }}
                      data-testid="kyc-resubmit"
                      className="mt-5 inline-flex items-center px-5 py-2.5 rounded-lg brand-gradient text-primary-foreground font-semibold text-[13px] hover:brightness-110">
                Re-upload documents
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
